/**
 * Funnel Tracking System - TypeScript Interfaces
 * 
 * Defines the schema for conversion pipeline tracking across different
 * traffic sources (municipalities, social media, email campaigns)
 */

// Base Event Interface
export interface FunnelEvent {
  event_id: string;
  timestamp: string; // ISO 8601 format
  event_type: 'step_enter' | 'step_complete' | 'step_exit' | 'interaction' | 'conversion' | 'dropout';
  step_id: string;
  data: Record<string, any>;
}

// Funnel Step Definition
export interface FunnelStep {
  step_id: string;
  step_name: string;
  step_order: number;
  required: boolean;
  auto_advance: boolean;
}

// Session Metrics
export interface SessionMetrics {
  total_duration_ms: number;
  steps_completed: number;
  interactions_count: number;
  conversion_score: number; // 0-1 scale
}

// Base Session Interface
export interface FunnelSession {
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  status: 'active' | 'completed' | 'abandoned' | 'converted';
  current_step: string;
  user_agent: string;
  ip_address: string; // Anonymized (last octet removed)
  referrer: string;
  events: FunnelEvent[];
  metrics: SessionMetrics;
}

// Municipality Token-based Funnel Session
export interface MunicipalityFunnelSession extends FunnelSession {
  municipality_id: string;
  municipality_name: string;
  token: string;
  scenario_data?: {
    current_scenario: 'geschlossen' | 'offen' | 'smart';
    scenario_changes: number;
    parameters_adjusted: number;
  };
}

// Social Media Campaign Session
export interface SocialMediaFunnelSession extends FunnelSession {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content?: string;
  utm_term?: string;
  platform: 'instagram' | 'facebook' | 'linkedin' | 'twitter';
  ad_id?: string;
  selected_municipality?: string;
}

// Email Campaign Session
export interface EmailFunnelSession extends FunnelSession {
  email_id: string;
  recipient_segment: string;
  campaign_id: string;
  email_provider: 'resend' | 'mailchimp' | 'sendgrid';
  link_clicked?: string;
}

// Gewerbe (Business) Funnel Session
export interface GewerbeFunnelSession extends FunnelSession {
  business_id: string;
  business_name: string;
  business_type: 'klein' | 'mittel' | 'groß';
  annual_consumption_kwh?: number;
  business_sector?: string;
  employee_count?: number;
  business_assessment_data?: {
    energy_costs_monthly: number;
    sustainability_goals: string[];
    current_energy_source: string;
    peak_demand_times: string[];
  };
}

// Teilnehmer (Participant) Funnel Session
export interface TeilnehmerFunnelSession extends FunnelSession {
  participant_id: string;
  household_size: number;
  annual_consumption_kwh?: number;
  current_energy_provider?: string;
  participation_interest_level: 'low' | 'medium' | 'high';
  participation_data?: {
    preferred_energy_source: string[];
    expected_monthly_savings: number;
    participation_type: 'consumer' | 'prosumer';
    installation_willingness: boolean;
  };
}

// Funnel Configuration
export interface FunnelConfig {
  retention_days: number;
  anonymization_after_days: number;
  session_timeout_minutes: number;
}

// Base Funnel Interface
export interface BaseFunnel {
  funnel_type: string;
  version: string;
  created_at: string;
  updated_at: string;
  config: FunnelConfig;
  funnel_steps: FunnelStep[];
}

// Municipality Token-based Funnel
export interface MunicipalityFunnel extends BaseFunnel {
  funnel_type: 'municipality_token';
  municipality_id: string;
  municipality_name: string;
  token: string;
  sessions: Record<string, MunicipalityFunnelSession>;
}

// Social Media Campaign Funnel
export interface SocialMediaFunnel extends BaseFunnel {
  funnel_type: 'campaign_based';
  campaign_name: string;
  platform: 'instagram' | 'facebook' | 'linkedin' | 'twitter';
  campaign_start: string;
  campaign_end: string;
  sessions: Record<string, SocialMediaFunnelSession>;
}

// Email Campaign Funnel
export interface EmailFunnel extends BaseFunnel {
  funnel_type: 'email_based';
  campaign_name: string;
  email_provider: 'resend' | 'mailchimp' | 'sendgrid';
  campaign_date: string;
  sessions: Record<string, EmailFunnelSession>;
}

// Funnel ID Pattern Types
export type FunnelIdPattern = 
  | `gemeinden_gemeinde_${string}` 
  | `gemeinden_social_${string}` 
  | `gemeinden_email_${string}`
  | `gewerbe_business_${string}` 
  | `gewerbe_social_${string}` 
  | `gewerbe_email_${string}`
  | `teilnehmer_participant_${string}` 
  | `teilnehmer_social_${string}` 
  | `teilnehmer_email_${string}`;

// Enhanced Funnel Interfaces with Funnel ID
export interface EnhancedMunicipalityFunnel extends BaseFunnel {
  funnel_type: 'municipality_token';
  funnel_id: FunnelIdPattern;
  municipality_id: string;
  municipality_name: string;
  token: string;
  business_type: 'gemeinden' | 'gewerbe' | 'teilnehmer';
  sessions: Record<string, MunicipalityFunnelSession>;
}

export interface EnhancedSocialMediaFunnel extends BaseFunnel {
  funnel_type: 'campaign_based';
  funnel_id: FunnelIdPattern;
  campaign_name: string;
  platform: 'instagram' | 'facebook' | 'linkedin' | 'twitter';
  campaign_start: string;
  campaign_end: string;
  business_type: 'gemeinden' | 'gewerbe' | 'teilnehmer';
  sessions: Record<string, SocialMediaFunnelSession>;
}

export interface EnhancedEmailFunnel extends BaseFunnel {
  funnel_type: 'email_based';
  funnel_id: FunnelIdPattern;
  campaign_name: string;
  email_provider: 'resend' | 'mailchimp' | 'sendgrid';
  campaign_date: string;
  business_type: 'gemeinden' | 'gewerbe' | 'teilnehmer';
  sessions: Record<string, EmailFunnelSession>;
}

// Hierarchical Funnel Collection Structure (V2)
export interface FunnelCollectionV2 {
  gemeinden: {
    funnel_type: 'gemeinden_service';
    version: string;
    created_at: string;
    updated_at: string;
    config: FunnelConfig;
    funnels: Record<FunnelIdPattern, EnhancedMunicipalityFunnel | EnhancedSocialMediaFunnel | EnhancedEmailFunnel>;
  };
  gewerbe: {
    funnel_type: 'business_service';
    version: string;
    created_at: string;
    updated_at: string;
    config: FunnelConfig;
    funnels: Record<FunnelIdPattern, EnhancedMunicipalityFunnel | EnhancedSocialMediaFunnel | EnhancedEmailFunnel>;
  };
  teilnehmer: {
    funnel_type: 'participant_service';
    version: string;
    created_at: string;
    updated_at: string;
    config: FunnelConfig;
    funnels: Record<FunnelIdPattern, EnhancedMunicipalityFunnel | EnhancedSocialMediaFunnel | EnhancedEmailFunnel>;
  };
  
  // Session mappings for quick lookups
  session_mappings?: {
    consultation_by_token?: Record<string, string>;
    agreement_by_session?: Record<string, string>;
  };
  
  // Compliance logs
  compliance_logs?: {
    data_exports?: Record<string, any>;
    deletions?: Record<string, any>;
  };
}

// Legacy Funnel Collection Structure (for backward compatibility)
export interface FunnelCollection {
  gemeinden: {
    funnel_type: 'municipality_token';
    version: string;
    created_at: string;
    updated_at: string;
    config: FunnelConfig;
    funnels: Record<string, MunicipalityFunnel>;
  };
  social_media: {
    funnel_type: 'campaign_based';
    version: string;
    created_at: string;
    updated_at: string;
    config: FunnelConfig;
    funnels: Record<string, SocialMediaFunnel>;
  };
  email_campaigns: {
    funnel_type: 'email_based';
    version: string;
    created_at: string;
    updated_at: string;
    config: FunnelConfig;
    funnels: Record<string, EmailFunnel>;
  };
}

// Predefined Funnel Steps for Different Types
export const MUNICIPALITY_FUNNEL_STEPS: FunnelStep[] = [
  {
    step_id: 'landing',
    step_name: 'Token Landing',
    step_order: 1,
    required: true,
    auto_advance: true
  },
  {
    step_id: 'analysis_view',
    step_name: 'EEG Analysis View',
    step_order: 2,
    required: true,
    auto_advance: false
  },
  {
    step_id: 'scenario_interaction',
    step_name: 'Scenario Selection',
    step_order: 3,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'parameter_adjustment',
    step_name: 'Parameter Customization',
    step_order: 4,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'callback_request',
    step_name: 'Callback Submitted',
    step_order: 5,
    required: false,
    auto_advance: false
  }
];

export const SOCIAL_MEDIA_FUNNEL_STEPS: FunnelStep[] = [
  {
    step_id: 'ad_click',
    step_name: 'Social Media Ad Click',
    step_order: 1,
    required: true,
    auto_advance: true
  },
  {
    step_id: 'landing_page',
    step_name: 'Landing Page View',
    step_order: 2,
    required: true,
    auto_advance: true
  },
  {
    step_id: 'municipality_selection',
    step_name: 'Municipality Selection',
    step_order: 3,
    required: true,
    auto_advance: false
  },
  {
    step_id: 'analysis_engagement',
    step_name: 'Analysis Engagement',
    step_order: 4,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'lead_generation',
    step_name: 'Contact Info Provided',
    step_order: 5,
    required: false,
    auto_advance: false
  }
];

export const EMAIL_FUNNEL_STEPS: FunnelStep[] = [
  {
    step_id: 'email_open',
    step_name: 'Email Opened',
    step_order: 1,
    required: true,
    auto_advance: true
  },
  {
    step_id: 'link_click',
    step_name: 'Link Clicked',
    step_order: 2,
    required: true,
    auto_advance: true
  },
  {
    step_id: 'content_engagement',
    step_name: 'Content Engagement',
    step_order: 3,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'action_taken',
    step_name: 'Action Taken',
    step_order: 4,
    required: false,
    auto_advance: false
  }
];

// Gewerbe (Business) Funnel Steps
export const GEWERBE_FUNNEL_STEPS: FunnelStep[] = [
  {
    step_id: 'business_landing',
    step_name: 'Business Landing Page',
    step_order: 1,
    required: true,
    auto_advance: true
  },
  {
    step_id: 'business_assessment',
    step_name: 'Business Energy Assessment',
    step_order: 2,
    required: true,
    auto_advance: false
  },
  {
    step_id: 'cost_analysis',
    step_name: 'Cost-Benefit Analysis',
    step_order: 3,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'roi_calculation',
    step_name: 'ROI Calculation',
    step_order: 4,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'business_consultation',
    step_name: 'Business Consultation Request',
    step_order: 5,
    required: false,
    auto_advance: false
  }
];

// Teilnehmer (Participant) Funnel Steps
export const TEILNEHMER_FUNNEL_STEPS: FunnelStep[] = [
  {
    step_id: 'participant_landing',
    step_name: 'Participant Landing Page',
    step_order: 1,
    required: true,
    auto_advance: true
  },
  {
    step_id: 'community_overview',
    step_name: 'Energy Community Overview',
    step_order: 2,
    required: true,
    auto_advance: false
  },
  {
    step_id: 'benefits_exploration',
    step_name: 'Benefits Exploration',
    step_order: 3,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'participation_calculator',
    step_name: 'Participation Calculator',
    step_order: 4,
    required: false,
    auto_advance: false
  },
  {
    step_id: 'registration_interest',
    step_name: 'Registration Interest',
    step_order: 5,
    required: false,
    auto_advance: false
  }
];

// Event Data Interfaces for specific event types
export interface StepEnterEventData {
  load_time_ms: number;
  viewport: string;
  device_type: 'desktop' | 'mobile' | 'tablet';
}

export interface StepCompleteEventData {
  time_on_step_ms: number;
  scroll_depth_percent: number;
}

export interface InteractionEventData {
  action: string;
  element_id?: string;
  value?: any;
  timestamp: string;
}

export interface ConversionEventData {
  conversion_type: 'callback' | 'lead' | 'signup' | 'contact';
  conversion_value?: number;
  additional_data?: Record<string, any>;
}

// Utility Types for API Responses
export type FunnelType = 'municipality_token' | 'campaign_based' | 'email_based';
export type EventType = 'step_enter' | 'step_complete' | 'step_exit' | 'interaction' | 'conversion' | 'dropout';
export type SessionStatus = 'active' | 'completed' | 'abandoned' | 'converted';
export type EEGScenario = 'geschlossen' | 'offen' | 'smart';
export type DeviceType = 'desktop' | 'mobile' | 'tablet';
export type SocialPlatform = 'instagram' | 'facebook' | 'linkedin' | 'twitter';
export type EmailProvider = 'resend' | 'mailchimp' | 'sendgrid';

// Enhanced Types for V2 Schema
export type BusinessType = 'gemeinden' | 'gewerbe' | 'teilnehmer';
export type ServiceType = 'gemeinden_service' | 'business_service' | 'participant_service';
export type FunnelCollectionType = FunnelCollection | FunnelCollectionV2;

// Funnel ID Generation Constants
export const FUNNEL_ID_PATTERNS = {
  gemeinden: {
    gemeinde: 'gemeinden_gemeinde_' as const,
    social: 'gemeinden_social_' as const,
    email: 'gemeinden_email_' as const,
  },
  gewerbe: {
    business: 'gewerbe_business_' as const,
    social: 'gewerbe_social_' as const,
    email: 'gewerbe_email_' as const,
  },
  teilnehmer: {
    participant: 'teilnehmer_participant_' as const,
    social: 'teilnehmer_social_' as const,
    email: 'teilnehmer_email_' as const,
  },
} as const;

// Funnel ID Utility Functions
export function generateFunnelId(
  businessType: BusinessType,
  sourceType: 'gemeinde' | 'business' | 'participant' | 'social' | 'email',
  identifier: string
): FunnelIdPattern {
  const pattern = FUNNEL_ID_PATTERNS[businessType][sourceType];
  return `${pattern}${identifier}` as FunnelIdPattern;
}

export function parseFunnelId(funnelId: FunnelIdPattern): {
  businessType: BusinessType;
  sourceType: 'gemeinde' | 'business' | 'participant' | 'social' | 'email';
  identifier: string;
} {
  const parts = funnelId.split('_');
  if (parts.length < 3) {
    throw new Error(`Invalid funnel ID format: ${funnelId}`);
  }
  
  const businessType = parts[0] as BusinessType;
  const sourceType = parts[1] as 'gemeinde' | 'business' | 'participant' | 'social' | 'email';
  const identifier = parts.slice(2).join('_');
  
  return { businessType, sourceType, identifier };
}

export function isFunnelCollectionV2(collection: FunnelCollectionType): collection is FunnelCollectionV2 {
  return 'gemeinden' in collection && 'gewerbe' in collection && 'teilnehmer' in collection;
}

// KV Store Constants
export const FUNNEL_COLLECTION_KEY = 'funnels:json';
export const FUNNEL_COLLECTION_V2_KEY = 'funnels:json'; // Use same key for direct migration

// Migration Constants
export const MIGRATION_BATCH_SIZE = 10;
export const MIGRATION_VERSION = '2.0.0';